import { useContext, useMemo, useRef, useState } from 'preact/hooks'
import FieldsContext from 'components/business/SurveyForm/Fields/fieldsContext'
import { ChoiceType, OtherChoiceValue } from 'scripts/type'
import { shuffle } from 'scripts/utils'
import { SelectProps } from 'components/ui/Select/type'
import BaseContext from 'contexts/base-context'

export default function useSelectProps(props: SelectProps) {
  const { type, options, className, choicesRandom, checkedOptions, onChange, min, max } = props
  const { interactive } = useContext(BaseContext)
  const { windowSizeMd } = useContext(FieldsContext)!
  const [otherValue, setOtherValue] = useState('')
  const optionRef = useRef<HTMLDivElement>(null)
  const displayOrder = useMemo(() => {
    const otherChoiceIndex = options.findIndex(({ type }) => type === ChoiceType.OtherChoice)
    const optionsWithoutOtherChoice = options.slice(0, otherChoiceIndex > -1 ? otherChoiceIndex : options.length)
    const indexArray = Array.from(optionsWithoutOtherChoice, (_, index) => index)
    const optionsWithoutOtherChoiceOrder = choicesRandom ? shuffle(indexArray) : indexArray

    return otherChoiceIndex > -1
      ? [...optionsWithoutOtherChoiceOrder, otherChoiceIndex]
      : optionsWithoutOtherChoiceOrder
  }, [options.length, choicesRandom])

  const otherChoiceIsChecked = useMemo(() => {
    const otherChoiceIndex = options.findIndex(({ type }) => type === ChoiceType.OtherChoice)
    return otherChoiceIndex > -1 ? checkedOptions?.includes(options[otherChoiceIndex].value) : false
  }, [checkedOptions])
  const hasOtherChoiceAndReadOnly = options.some(({ type }) => type === ChoiceType.OtherChoice) && !interactive
  const showOtherChoiceInput = otherChoiceIsChecked || hasOtherChoiceAndReadOnly

  const handleOptionChange = (value: string, checked: boolean, choiceType: ChoiceType) => {
    if (type === 'checkbox') {
      const result = checked
        ? [...(checkedOptions ?? []), value]
        : checkedOptions?.filter((option: string) => option !== value)

      onChange({
        value: result,
        other: result?.includes(OtherChoiceValue) ? otherValue : undefined,
      })
    } else {
      onChange({
        value: checked ? [value] : [],
        other: choiceType === ChoiceType.OtherChoice ? (checked ? otherValue : '') : undefined,
      })
    }
    if (checked && choiceType === ChoiceType.OtherChoice) {
      setTimeout(() => {
        const htmlInputElement = optionRef?.current?.querySelector('input[type="text"]') as HTMLInputElement
        htmlInputElement?.focus()
      })
    }
  }

  const handleOtherValueChange = (value: string) => {
    setOtherValue(value)
    onChange({
      value: checkedOptions ?? [],
      other: value,
    })
  }

  return {
    handleOptionChange,
    handleOtherValueChange,
    otherChoiceIsChecked,
    showOtherChoiceInput,
    windowSizeMd,
    className,
    displayOrder,
    optionRef,
    checkedOptions,
    options,
    type,
    min,
    max,
  }
}
